/**
 * HTTP Request库
 * 支持：`fetch API`和`XMLHttpRequest`请求方式
 * 支持：`timeout`和`abort`机制
 * 支持：`GET` `HEAD` `POST` `PUT` `PATCH` `DELETE` `OPTIONS`，并有对应的快捷方法
 * 支持：错误自动捕捉与处理
 * 支持：统一的`Response`
 * 支持：跨域请求
 * 支持：传递`Cookie`凭证
 * 支持：`json`-JSON `text`-纯文本 `urlencoded`-编码的URL `form`-表单等数据提交方式
 * 支持：`json`-JSON `text`-纯文本 `blob`-文件对象等数据响应方式
 * 返回Promise对象，于是支持async和await异步编程方式
 * @author luoying<luoying@szzbmy.com>
 * @since 18/11/16
 */
import * as URL from './url';
import * as qs from './query-string';
import { globalEvent } from './events';
// csrfToken附着的meta元素
const csrfMeta = document.head.querySelector('meta[name="csrf-token"]');
// csrfToken
const csrfToken = (csrfMeta && csrfMeta.content) || '';
// 需要经过csrf检测的请求方法
const csrfMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
const requestHookTypes = {
    success: 'request:success',
    error: 'request:error',
    complete: 'request:complete',
};
/**
 * 全局捕捉所有成功请求
 * @param {((data: T, req: Request<T>) => void)} cb 回调函数，任意成功请求，都会执行此回调函数
 * @example
 * catchSuccess((data, req) => {
 *  if (req.url === '/api/user/1234') {
 *    console.log(data);
 *  }
 * });
 */
export function catchSuccess(cb) {
    globalEvent.on(requestHookTypes.success, evt => {
        cb(evt.data, evt.req);
    });
}
/**
 * 全局捕捉所有失败请求
 * @param {(err: FetchError, req: Request<T>) => void} cb 回调函数，任意失败请求，都会执行此回调函数
 * @example
 * catchError(err => {
 *  // 403 - 用户未登录
 *  if (err.status === 403) {
 *    showLoginDialog();
 *  }
 * });
 */
export function catchError(cb) {
    globalEvent.on(requestHookTypes.error, evt => {
        evt.error && cb(evt.error, evt.req);
    });
}
/**
 * 全局捕捉所有请求，无论成功还是失败
 * @param {((err: FetchError | null, data: T, req: Request<T>) => void)} cb 回调函数，任意请求结果，都会执行此回调函数
 */
export function catchComplete(cb) {
    globalEvent.on(requestHookTypes.complete, evt => {
        cb(evt.error, evt.data, evt.req);
    });
}
// dataType与Content-Type的映射关系
const mimeTypes = {
    json: 'application/json',
    text: 'text/plain',
    urlencoded: 'application/x-www-form-urlencoded',
    form: 'multipart/form-data',
};
const SUCCESS_STATUS = 200;
const NO_CONTENT_STATUS = 204;
const X_300_STATUS = 300;
const CACHE_STATUS = 304;
const TIMEOUT_STATUS = 504;
const TIMEOUT_CODE = 1001;
const TIMEOUT_MSG = '请求超时';
const SERVICE_ERROR_STATUS = 500;
const SERVICE_ERROR_CODE = 1000;
const SERVICE_ERROR_MSG = '未知错误';
const ABORT_STATUS = 400;
const ABORT_CODE = 1002;
const ABORT_MSG = '请求被取消';
export class FetchError extends Error {
    constructor(code, status, msg, stack, nativeCode) {
        super(msg);
        this.status = SUCCESS_STATUS;
        this.code = SUCCESS_STATUS;
        this.nativeCode = null;
        this.code = code;
        this.status = status;
        if (stack)
            this.stack = stack;
        if (nativeCode)
            this.nativeCode = nativeCode;
    }
}
class Request {
    constructor(url, data, options) {
        this._url = '';
        this._type = 'json';
        this._dataType = 'json';
        this.data = null;
        this.headers = {
            Accept: mimeTypes.json,
            'Content-Type': mimeTypes.json,
        };
        this.method = 'GET';
        this.timeout = 0;
        this.useXMLHttpRequest = false;
        this.cors = false;
        this.credentials = false;
        // fetch API模拟超时的计时器
        this.fetchTimeoutTimer = 0;
        this.handleProgress = (evt) => {
            if (this.onUploadProgress) {
                this.onUploadProgress(evt);
            }
            if (this.onProgress) {
                this.onProgress(evt.loaded, evt.total);
            }
        };
        // 兼容旧属性`withCredentials`，优先使用新属性`credentials`
        if (options.withCredentials !== void 0 && options.credentials === void 0) {
            options.credentials = options.withCredentials;
        }
        // 兼容旧属性`useAjax`，优先使用新属性`useXMLHttpRequest`
        if (options.useAjax !== void 0 && options.useXMLHttpRequest === void 0) {
            options.useXMLHttpRequest = options.useAjax;
        }
        if (options.method)
            this.method = options.method;
        if (options.type)
            this.type = options.type;
        if (options.dataType)
            this.dataType = options.dataType;
        if (options.contentType !== void 0)
            this.contentType = options.contentType;
        if (options.timeout !== void 0)
            this.timeout = Math.max(0, options.timeout);
        if (options.useXMLHttpRequest !== void 0)
            this.useXMLHttpRequest = options.useXMLHttpRequest;
        if (options.credentials !== void 0)
            this.credentials = options.credentials;
        this.onProgress = options.onProgress;
        this.onUploadProgress = options.onUploadProgress;
        this.proxy = options.proxy;
        this.setUrl(this.resolveUrl(url, data));
        this.data = this.resolveData(data);
        // 默认情况下，非跨域请求，强制传递cookie凭证
        if (!this.cors)
            this.credentials = true;
        if (options.headers)
            Object.assign(this.headers, options.headers);
        /**
         * 满足以下条件时，需要附加csrf token到请求头
         * 1. 本站请求
         * 2. 请求方法是['POST', 'PUT', 'PATCH', 'DELETE']中的一种
         * 3. 网页中存在csrf token
         */
        const isSameSiteReq = URL.isSameSiteUrl(url);
        const isCsrfReq = options.method && csrfMethods.includes(options.method);
        if (isSameSiteReq && isCsrfReq) {
            const token = options.csrfToken || csrfToken;
            if (token)
                this.setHeader('X-CSRF-Token', token);
        }
        this._options = options;
    }
    resolveUrl(url, data) {
        // GET/HEAD请求带了data参数，需要格式化成query-string
        if (this.isQueryRequest() && data) {
            if (typeof data === 'string') {
                url += /^\?/.test(data) ? data : `?${data}`;
            }
            else {
                url += `?${qs.stringify(data)}`;
            }
        }
        return url;
    }
    get options() {
        return this._options;
    }
    get url() {
        return this._url;
    }
    setUrl(val) {
        this._url = val;
        // 绝对地址正则，匹配以下绝对地址
        // `http://xxx`
        // `https://xxx`
        // `//xxx`
        if (/^(?:http[s]?:)?\/\/(.*?)/i.test(val)) {
            const host = URL.parse(val).host;
            // 当api接口域名与当前页面域名不一致，说明存在跨域请求
            // 跨域请求，设置cors属性为true
            if (host !== window.location.hostname) {
                this.cors = true;
            }
        }
    }
    resolveData(data) {
        // GET/HEAD请求，参数数据已序列化在url上
        if (this.isQueryRequest() || data == null) {
            return null;
        }
        // 需要处理的JSON数据，JSON格式化
        if (this.type === 'json') {
            return JSON.stringify(data);
        }
        // url编码的表单数据，querystring格式化
        if (this.type === 'urlencoded') {
            const obj = data;
            return qs.stringify(obj);
        }
        return data;
    }
    get type() {
        return this._type;
    }
    set type(val) {
        // 当传入不认识的`type`，使用`json`代替
        if (!['json', 'text', 'urlencoded', 'form'].includes(val)) {
            val = 'json';
        }
        this._type = val;
        if (val === 'form') {
            this.contentType = false;
            return;
        }
        this.contentType = mimeTypes[val];
    }
    get dataType() {
        return this._dataType;
    }
    set dataType(val) {
        // 当传入不认识的`dataType`，使用`json`代替
        if (!['json', 'text', 'blob'].includes(val)) {
            val = 'json';
        }
        this._dataType = val;
        const accept = val !== 'blob' ? mimeTypes[val] : '*/*';
        this.setHeader('Accept', accept);
    }
    set contentType(val) {
        if (val === false) {
            this.removeHeader('Content-Type');
            return;
        }
        this.setHeader('Content-Type', val);
    }
    setHeader(key, val) {
        this.headers[key] = val;
    }
    removeHeader(key) {
        delete this.headers[key];
    }
    isQueryRequest() {
        return ['GET', 'HEAD'].includes(this.method);
    }
    request() {
        if (window.fetch && !this.useXMLHttpRequest) {
            return this.requestByFetchApi();
        }
        return this.requestByXMLHttpRequest();
    }
    async requestByFetchApi() {
        const settings = {
            body: this.data != null ? this.data : undefined,
            method: this.method,
            headers: this.headers,
            credentials: this.credentials ? 'include' : undefined,
            mode: this.cors ? 'cors' : undefined,
            cache: 'default',
        };
        const tasks = [];
        // 超时限制
        if (this.timeout)
            tasks.push(this.createFetchTimeout());
        // 取消请求
        const { promise: abortPromise, abort } = this.createFetchAbort();
        tasks.push(abortPromise);
        const fetchPromise = window
            .fetch(this.url, settings)
            .then(res => this.handleFetchLoad(res))
            .catch(err => {
            this.finallyFetch();
            throw err;
        });
        tasks.push(fetchPromise);
        // fetch、timeout、abort三个promise，任意一个率先改变状态，即表示请求结束
        const promise = Promise.race(tasks);
        if (this.proxy)
            this.proxy.abort = abort.bind(this);
        return promise;
    }
    async requestByXMLHttpRequest() {
        const xhr = new XMLHttpRequest();
        // 设置是否需要发送cookie凭证
        xhr.withCredentials = this.credentials;
        xhr.open(this.method, this.url);
        xhr.responseType = this.dataType;
        for (const name in this.headers) {
            xhr.setRequestHeader(name, this.headers[name]);
        }
        xhr.upload.onprogress = this.handleProgress;
        // 包装Promise异步模式
        const promise = new Promise((resolve, reject) => {
            // 错误事件捕捉
            xhr.onerror = this.handleXMLHttpRequestError(reject);
            // 取消请求事件捕捉
            xhr.onabort = this.handleXMLHttpRequestAbort(reject);
            // 超时限制
            if (this.timeout) {
                xhr.timeout = this.timeout;
                xhr.ontimeout = this.handleXMLHttpRequestTimeout(reject);
            }
            xhr.onreadystatechange = this.handleXMLHttpRequestReadyStateChange(resolve, reject);
        });
        xhr.send(this.data);
        if (this.proxy)
            this.proxy.abort = xhr.abort.bind(xhr);
        return promise;
    }
    handleFetchLoad(res) {
        return new Promise(async (resolve, reject) => {
            this.finallyFetch();
            try {
                const status = res.status;
                if (this.isNoContentResponse(status)) {
                    return resolve();
                }
                const result = await this.parseFetchData(res);
                const response = this.parseResponseData(result);
                // <200||>=300异常http status，抛出错误
                if (this.isHttpStatusException(status)) {
                    throw this.createHttpStatusError(response, res);
                }
                // 当业务处理不成功，抛出错误
                if (!response.success) {
                    throw this.createApiError(response);
                }
                resolve(response.data);
            }
            catch (err) {
                reject(err);
            }
        });
    }
    async parseFetchData(res) {
        const parsers = {
            async json() {
                const data = await res.json();
                return data;
            },
            text() {
                return res.text();
            },
            blob() {
                return res.blob();
            },
        };
        const parser = parsers[this.dataType];
        return await parser();
    }
    createFetchAbort() {
        let abort;
        const promise = new Promise((_resolve, reject) => {
            abort = () => {
                // #1002: 客户端错误代码，请求被取消
                const err = new FetchError(ABORT_CODE, ABORT_STATUS, ABORT_MSG);
                reject(err);
                this.finallyFetch();
            };
        });
        return { promise, abort };
    }
    createFetchTimeout() {
        return new Promise((_resolve, reject) => {
            this.fetchTimeoutTimer = window.setTimeout(() => {
                // #1001: 客户端错误代码，请求超时
                const err = new FetchError(TIMEOUT_CODE, TIMEOUT_STATUS, TIMEOUT_MSG);
                reject(err);
                this.finallyFetch();
            }, this.timeout);
        });
    }
    finallyFetch() {
        if (this.fetchTimeoutTimer) {
            window.clearTimeout(this.fetchTimeoutTimer);
            this.fetchTimeoutTimer = 0;
        }
        if (this.proxy)
            this.proxy.abort = null;
    }
    handleXMLHttpRequestReadyStateChange(resolve, reject) {
        const that = this;
        return function () {
            const xhr = this;
            const status = xhr.status;
            if (xhr.readyState !== xhr.DONE)
                return;
            that.finallyXMLHttpRequest(xhr);
            if (that.isNoContentResponse(status)) {
                return resolve();
            }
            try {
                const result = that.parseXMLHttpRequstData(xhr);
                const response = that.parseResponseData(result);
                // <200||>=300异常http status，抛出错误
                if (that.isHttpStatusException(status)) {
                    throw that.createHttpStatusError(response, xhr);
                }
                // 当业务处理不成功，抛出错误
                if (!response.success) {
                    throw that.createApiError(response);
                }
                resolve(response.data);
            }
            catch (err) {
                reject(err);
            }
        };
    }
    parseXMLHttpRequstData(xhr) {
        const parsers = {
            json() {
                let data;
                if (xhr.response) {
                    data = xhr.response;
                }
                else {
                    const text = xhr.responseText;
                    data = text ? JSON.parse(text) : null;
                }
                return data;
            },
            text() {
                return xhr.responseText;
            },
            blob() {
                return new Blob([xhr.response]);
            },
        };
        return parsers[this.dataType]();
    }
    handleXMLHttpRequestError(reject) {
        const that = this;
        return function () {
            const err = new FetchError(SERVICE_ERROR_CODE, SERVICE_ERROR_STATUS, SERVICE_ERROR_MSG);
            reject(err);
            that.finallyXMLHttpRequest(this);
        };
    }
    handleXMLHttpRequestAbort(reject) {
        const that = this;
        return function () {
            // #1002: 客户端错误代码，请求被取消
            const err = new FetchError(ABORT_CODE, ABORT_STATUS, ABORT_MSG);
            reject(err);
            that.finallyXMLHttpRequest(this);
        };
    }
    handleXMLHttpRequestTimeout(reject) {
        const that = this;
        return function () {
            // #1001: 客户端错误代码，请求超时
            const err = new FetchError(TIMEOUT_CODE, TIMEOUT_STATUS, TIMEOUT_MSG);
            reject(err);
            that.finallyXMLHttpRequest(this);
        };
    }
    finallyXMLHttpRequest(xhr) {
        // 释放xhr
        xhr.upload.onprogress = null;
        xhr.ontimeout = xhr.onload = xhr.onerror = xhr.onabort = null;
        if (this.proxy)
            this.proxy.abort = null;
    }
    isNoContentResponse(status) {
        // head请求，无响应体
        // 204 http status，无响应体
        return this.method === 'HEAD' || status === NO_CONTENT_STATUS;
    }
    isHttpStatusException(status) {
        return (status < SUCCESS_STATUS ||
            (status >= X_300_STATUS && status !== CACHE_STATUS));
    }
    createHttpStatusError(response, res) {
        const status = res.status;
        const code = response.errorcode || status;
        const msg = response.errormsg || res.statusText || SERVICE_ERROR_MSG;
        const stack = response.errorstack;
        return new FetchError(code, status, msg, stack, response.nativecode);
    }
    createApiError(response) {
        const code = response.errorcode;
        const status = response.statuscode;
        const msg = response.errormsg || SERVICE_ERROR_MSG;
        const stack = response.errorstack;
        return new FetchError(code, status, msg, stack, response.nativecode);
    }
    parseResponseData(result) {
        if (this.dataType === 'json') {
            const res = result;
            let responseData;
            if (res === null) {
                responseData = this.createResponseData(null);
            }
            else if ('success' in res) {
                responseData = res;
            }
            else if ('error_code' in res) {
                responseData = this.parseOldResponseData(res);
            }
            else {
                responseData = this.createResponseData(res);
            }
            return responseData;
        }
        return this.createResponseData(result);
    }
    parseOldResponseData(result) {
        const success = !result.error_code;
        return {
            success,
            statuscode: success ? SUCCESS_STATUS : SERVICE_ERROR_STATUS,
            errorcode: result.error_code,
            errormsg: result.error,
            errorstack: null,
            nativecode: null,
            data: result.result,
        };
    }
    createResponseData(data) {
        return {
            success: true,
            statuscode: SUCCESS_STATUS,
            errorcode: 0,
            errormsg: null,
            errorstack: null,
            nativecode: null,
            data,
        };
    }
}
/**
 * HTTP请求
 * @param  {string} url 请求地址
 * @param  {RequestData} [data=null] 请求数据，可以是任意数据类型
 * @param  {RequestOptions} [options={}] 其他数据选项，比如`method` `headers`等
 * @return {Promise<T>} 返回一个Promise对象，调用方自行捕捉异常，也可以用于async和await语法
 * @example
 * const data = await fetch('/api/user/1234', null, { method: 'GET' })
 */
export async function fetch(url, data = null, options = {}) {
    const req = new Request(url, data, options);
    try {
        const data = await req.request();
        const event = { error: null, data, req };
        globalEvent.fire(requestHookTypes.success, event);
        globalEvent.fire(requestHookTypes.complete, event);
        return data;
    }
    catch (error) {
        const event = { error, data: null, req };
        globalEvent.fire(requestHookTypes.error, event);
        globalEvent.fire(requestHookTypes.complete, event);
        throw error;
    }
}
/**
 * 获取数据，这是幂等请求
 * @param  {string} url 请求地址
 * @param  {RequestData} [data] query参数，可以是格式化后的query-string，也可以是未格式化的query对象
 * @param  {RequestOptions} [options={}] 额外数据选项，例如 `headers`等
 * @return {Promise<T>} 返回一个Promise对象，调用方自行捕捉异常
 * @example
 * const user = await get('/api/user/1234')
 */
export function get(url, data, options = {}) {
    options.method = 'GET';
    return fetch(url, data, options);
}
/**
 * HEAD方法请求一个与GET请求的响应相同的响应，但没有响应体。这是幂等请求
 * @param  {string} url 请求地址
 * @param  {RequestData} [data] query参数，可以是格式化后的query-string，也可以是未格式化的query对象
 * @param  {RequestOptions} [options={}] 额外数据选项，例如 `headers`等
 * @example
 * await head('/api/user/1234')
 */
export function head(url, data, options = {}) {
    options.method = 'HEAD';
    fetch(url, data, options);
}
/**
 * 提交数据，用于新建资源，这是非幂等请求
 * @param  {string} url 提交地址
 * @param  {RequestData} [data] 提交到服务器的数据，可以是任意数据类型
 * @param  {RequestOptions} [options={}] 额外数据选项，例如 `headers`等
 * @return {Promise<T>} 返回一个Promise对象，调用方自行捕捉异常
 * @example
 * const id = await post('/api/user', { name: 'test', age: 32 })
 */
export function post(url, data, options = {}) {
    options.method = 'POST';
    return fetch(url, data, options);
}
/**
 * 更新数据，用于替换资源，这是幂等请求
 * @param  {string} url 提交地址
 * @param  {RequestData} [data] 提交到服务器的数据，可以是任意数据类型
 * @param  {RequestOptions} [options={}] 额外数据选项，例如 `headers`等
 * @return {Promise<T>} 返回一个Promise对象，调用方自行捕捉异常
 * @example
 * await put('/api/user/1234', { name: 'test', age: 22 })
 */
export function put(url, data, options = {}) {
    options.method = 'PUT';
    return fetch(url, data, options);
}
/**
 * 类似于PUT请求，但只用于局部修改，这是非幂等请求
 * @param {string} url 提交地址
 * @param {RequestData} [data] 提交到服务器的数据，可以是任意数据类型
 * @param {RequestOptions} [options={}] 额外数据选项，例如 `headers`等
 * @returns {Promise<T>} 返回一个Promise对象，调用方自行捕捉异常
 * @example
 * const user = await patch('/api/user/1234', { age: 28 })
 */
export function patch(url, data, options = {}) {
    options.method = 'PATCH';
    return fetch(url, data, options);
}
/**
 * 删除数据，这是幂等请求
 * @param  {string} url 请求地址
 * @param  {RequestData} [data] 提交到服务器的数据，可以是任意数据类型
 * @param  {RequestOptions} [options={}] 额外数据选项，例如 `headers`等
 * @return {Promise<T>} 返回一个Promise对象，调用方自行捕捉异常
 * @example
 * const success = await del('/api/user/1234')
 */
export function del(url, data, options = {}) {
    options.method = 'DELETE';
    return fetch(url, data, options);
}
/**
 * OPTIONS请求，常用于跨域请求时询问，这是幂等请求
 * @param  {string} url 请求地址
 * @param  {RequestOptions} [options={}] 额外数据选项，例如 `headers`等
 * @return {Promise<T>} 返回一个Promise对象，调用方自行捕捉异常
 * @example
 * const res = await options('/api/user')
 */
export function options(url, options = {}) {
    options.method = 'OPTIONS';
    options.dataType = 'text';
    return fetch(url, null, options);
}
/**
 * 上传数据包
 * @param  {string} url 上传地址
 * @param  {FormData} data 数据包（FormData对象）
 * @param  {RequestOptions} [options={}] 额外的数据选项
 * @return {Promise<T>} 返回一个Promise对象，调用方自行捕捉异常
 * @example
 * const formData = new FormData();
 * formData.append('image', blob);
 * const data = await upload('/api/upload/image', formData)
 */
export function upload(url, data, options = {}) {
    options.type = 'form';
    return post(url, data, options);
}
//# sourceMappingURL=request.js.map